import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SOCIALS } from "../data/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-neutral-200 bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3" onClick={() => setOpen(false)}>
          <img
            src="/images/abs-logo-mark-light-bg.png"
            alt="ABS LLC — Alex Brosseau Services"
            width="600"
            height="202"
            className="h-10 w-auto md:h-12"
          />
        </Link>

        <nav data-testid="nav-desktop" className="hidden items-center gap-4 2xl:gap-5 xl:flex" aria-label="Primary">
          {NAV_LINKS.filter((link) => link.to !== "/").map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 hover:text-[#e63946] 2xl:text-[12px] 2xl:tracking-[0.14em] ${
                  isActive ? "text-[#111111]" : "text-neutral-500"
                }`
              }
            >
              {link.short || link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="nav-socials" className="hidden items-center gap-0.5 2xl:flex">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.aria}
                title={s.label}
                data-testid={`nav-social-${s.name.toLowerCase()}`}
                className="flex h-10 w-10 items-center justify-center text-neutral-400 transition-colors duration-300 hover:text-[#e63946]"
              >
                <s.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="nav-inquiry-button"
            className="group hidden items-center gap-2 whitespace-nowrap bg-[#111111] px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-[#e63946] md:inline-flex"
          >
            Start an Inquiry
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center border border-neutral-300 text-[#111111] transition-colors duration-300 hover:border-[#111111] xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-neutral-200 bg-white xl:hidden"
            aria-label="Mobile"
          >
            <div className="px-5 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b border-neutral-100 py-4 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-[#e63946]" : "text-[#111111]"
                      }`
                    }
                  >
                    {link.label}
                    <ArrowUpRight className="h-5 w-5 text-neutral-400" />
                  </NavLink>
                </motion.div>
              ))}
              <div data-testid="nav-mobile-socials" className="mt-6 flex items-center gap-2 border-t border-neutral-100 pt-5">
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.aria}
                    title={s.label}
                    data-testid={`nav-mobile-social-${s.name.toLowerCase()}`}
                    className="flex h-11 w-11 items-center justify-center border border-neutral-300 text-neutral-500 transition-colors duration-300 hover:border-[#e63946] hover:text-[#e63946]"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
                <span className="font-overline ml-2 text-[9px] uppercase tracking-[0.3em] text-neutral-400">
                  Follow ABS
                </span>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
